import { formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, inject, input, LOCALE_ID, signal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../main/type.map.service";
import { FileUploadComponent, S25File } from "../modules/s25-file-upload/s25.file.upload.component";
import { FileUploadModule } from "../modules/s25-file-upload/s25.file.upload.module";
import { GenericTableButtonComponent } from "../modules/s25-table/generics/generic.table.button.component";
import { Table } from "../modules/s25-table/Table";
import { S25TableModule } from "../modules/s25-table/s25.table.module";
import { AttachmentService, FileItem } from "../services/attachment.service";
import { S25DisplayFileComponent } from "./s25.display.file.component";
import { S25IconButtonComponent } from "./s25.icon.button.component";
import { AlertService } from "../services/alert.service";
import { S25Util } from "../util/s25-util";
import { FILESTORE_POOL_MAX_FILES_PER_OBJECT } from "../util/filestore.util";
/**
 * Result of the optional delete guard: blocking references (e.g. custom attributes still pointing at the file).
 */
export type ObjectFilestorePoolDeleteGuardResult = {
    blockingCount: number;
    blockingAttributeLabels?: string[];
};

/**
 * Optional delete guard: return blocking refs (0 = safe to delete).
 * Used for custom-attribute pool references, etc.
 */
export type ObjectFilestorePoolDeleteGuard = (file: FileItem) => Promise<ObjectFilestorePoolDeleteGuardResult>;

/**
 * Filestore “pool” UI (list, upload, delete) for any object type / id.
 * Permissions and object-specific copy live in a thin parent (e.g. {@link SeOrgFilestoreComponent}).
 */
@TypeManagerDecorator("s25-ng-object-filestore-pool")
@Component({
    selector: "s25-ng-object-filestore-pool",
    template: `
        @if (canView()) {
            <section class="s25-object-filestore-pool">
                <div class="pool-toolbar">
                    <div class="pool-toolbar__start">
                        @if (introText(); as intro) {
                            <p class="text-muted">{{ intro }}</p>
                        }
                    </div>
                    <s25-ng-icon-button
                        type="refresh"
                        [ariaLabel]="'Refresh file list'"
                        [disabled]="loading()"
                        (clicked)="refreshFiles()"
                    />
                </div>
                @if (loadError()) {
                    <p class="ngRed">{{ loadError() }}</p>
                }
                @if (loading()) {
                    <p class="pool-loading">Loading files…</p>
                } @else if (fileTableData) {
                    <s25-ng-table
                        class="object-filestore-pool-table"
                        [dataSource]="fileTableData"
                        [hasFilter]="true"
                        [columnSortable]="true"
                        [hasTotalRowCount]="true"
                        [hasExport]="true"
                        [exportFilename]="'file-pool-files.xlsx'"
                        [exportSheetName]="'Files'"
                        [caption]="'Files'"
                        [noResultsMessage]="'No files uploaded yet.'"
                        [showHeaderWhenNoData]="true"
                        [pivotThreshold]="400"
                    />
                }
                @if (canManage() && !loading()) {
                    <div class="pool-upload-block">
                        <div class="pool-upload-cell">
                            <s25-ng-file-upload
                                [showFiles]="false"
                                [multiple]="false"
                                [files]="uploadStash"
                                [onAdd]="handlePoolFileAdded"
                                [extRegex]="AttachmentService.FILE_EXTENSION_REGEX"
                            />
                            @if (uploading()) {
                                <span class="pool-upload-status">Uploading…</span>
                            }
                            @if (uploadError()) {
                                <span class="ngRed pool-upload-status">{{ uploadError() }}</span>
                            }
                        </div>
                    </div>
                }
            </section>
        }
    `,
    styles: `
        .s25-object-filestore-pool {
            margin-top: 1.5rem;
            padding: 1rem;
            border: 1px solid #e5e5e5;
            border-radius: 8px;
        }
        .text-muted {
            color: var(--color--text-muted);
            font-size: 0.9rem;
        }
        .pool-toolbar {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.75rem;
            margin-bottom: 0.75rem;
        }
        .pool-toolbar__start {
            flex: 1;
            min-width: 0;
        }
        .pool-loading {
            margin: 0.5rem 0 0;
            color: var(--color--text-muted);
            font-size: 0.9rem;
        }
        :host ::ng-deep .object-filestore-pool-table .s25ngTable {
            border: none;
            box-shadow: none;
        }
        :host ::ng-deep .object-filestore-pool-table .file-cell ::ng-deep .file-wrapper {
            flex-wrap: wrap;
            gap: 0.5rem 1rem;
        }
        :host ::ng-deep .object-filestore-pool-table .uploaded-cell {
            white-space: nowrap;
            font-size: 0.85rem;
            color: var(--color--text-muted);
        }
        .pool-upload-block {
            margin-top: 0.75rem;
            padding: 0.75rem;
            background: var(--color--surface-2-bg);
            border-radius: 6px;
            border: 1px solid var(--color--border);
        }
        .pool-upload-cell {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }
        .pool-upload-cell ::ng-deep .drop-zone {
            margin-bottom: 6px;
            padding: 10px;
            font-size: 0.9rem;
        }
        .pool-upload-status {
            font-size: 0.85rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FileUploadModule, S25IconButtonComponent, S25TableModule],
})
export class S25ObjectFilestorePoolComponent {
    /** For template binding to file extension allowlist. */
    protected readonly AttachmentService = AttachmentService;

    private readonly locale = inject(LOCALE_ID);
    private readonly fileUploadWidget = viewChild(FileUploadComponent);

    /** Bound to {@link FileUploadComponent}; cleared via {@link FileUploadComponent#reset}. */
    readonly uploadStash: S25File[] = [];

    /** Set after a successful load; drives {@code s25-ng-table}. */
    protected fileTableData: Table.DataSource | undefined;
    readonly objectTypeId = input.required<number>();
    readonly objectId = input.required<number>();
    readonly canView = input.required<boolean>();
    readonly canManage = input.required<boolean>();

    /** Optional lead paragraph (e.g. explain pool usage for this object type). */
    readonly introText = input<string | undefined>(undefined);
    readonly uploadAriaLabel = input("Upload file");
    /** Noun for delete copy, e.g. "organization" → "from organization files". */
    readonly filesGroupLabel = input("object");
    readonly fileKey = input<string | undefined>(undefined);
    readonly deleteGuard = input<ObjectFilestorePoolDeleteGuard | undefined>(undefined);
    /** Appended after the delete question when non-empty (e.g. caveat about attribute refs). */
    readonly deleteConfirmNote = input<string | undefined>(undefined);

    protected readonly files = signal<FileItem[] | undefined>(undefined);
    protected readonly loading = signal(false);
    protected readonly loadError = signal<string | undefined>(undefined);
    protected readonly uploading = signal(false);
    protected readonly uploadError = signal<string | undefined>(undefined);

    constructor() {
        effect(() => {
            const id = this.objectId();
            const v = this.canView();
            if (!id || v !== true) {
                return;
            }
            this.loadFiles();
        });
    }

    protected refreshFiles(): void {
        void this.loadFiles();
    }

    private async loadFiles(): Promise<void> {
        const id = this.objectId();
        const ot = this.objectTypeId();
        if (!id) {
            return;
        }
        this.loading.set(true);
        this.loadError.set(undefined);
        const [list, err] = await S25Util.Maybe(AttachmentService.getFileList(ot, id, this.fileKey()));
        this.loading.set(false);
        if (err) {
            this.loadError.set(S25Util.errorText(err, "Could not load files."));
            this.fileTableData = undefined;
            return;
        }
        this.files.set(list ?? []);
        this.fileTableData = this.createFileTableDataSource();
    }

    private createFileTableDataSource(): Table.Unpaginated {
        const columns: Table.Column[] = [
            {
                id: "file",
                header: "File",
                minWidth: 200,
                sortable: true,
                cells: {
                    className: "file-cell",
                    component: S25DisplayFileComponent,
                },
            },
            {
                id: "uploaded",
                header: "Uploaded",
                minWidth: 140,
                sortable: true,
                cells: {
                    className: "uploaded-cell",
                    text: "",
                },
            },
        ];
        if (this.canManage()) {
            columns.push(
                GenericTableButtonComponent.Column(
                    "Delete",
                    async (row) => {
                        const f = row.data as FileItem;
                        if (f) await this.onDeleteClick(f);
                    },
                    "danger--outline",
                    "min-content",
                    undefined,
                    "Actions",
                ),
            );
        }
        return {
            type: "unpaginated",
            dataSource: this.getFileTableRows,
            columns,
        };
    }

    private readonly getFileTableRows = async (_query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> => {
        const list = this.files() ?? [];
        const rows = list.map((f) => this.fileItemToRow(f));
        return { rows, totalRows: rows.length };
    };

    private fileItemToRow(f: FileItem): Table.Row {
        const ot = this.objectTypeId();
        const oid = this.objectId();
        const fk = this.fileKeyFor(f);
        const name = f.itemName;
        const uploadedText = this.formatUploaded(f);
        return {
            id: name,
            name,
            data: f,
            cells: {
                file: {
                    component: S25DisplayFileComponent,
                    inputs: {
                        fileName: name,
                        objectType: ot,
                        objectId: oid,
                        fileKey: fk,
                    },
                    textValue: name,
                    filterValue: name,
                    sortValue: name,
                },
                uploaded: {
                    text: uploadedText,
                    sortValue: f.createDt ? new Date(f.createDt).toISOString() : "",
                    filterValue: uploadedText,
                },
            },
        };
    }

    private formatUploaded(f: FileItem): string {
        if (!f.createDt) {
            return "—";
        }
        return formatDate(f.createDt, "short", this.locale);
    }

    readonly handlePoolFileAdded = async (file: S25File): Promise<void> => {
        const nameErr = this.validatedUploadName(file);
        if (nameErr) {
            await AlertService.alert(nameErr);
            this.fileUploadWidget()?.reset();
            return;
        }
        const list = this.files() ?? [];
        if (list.some((x) => x.itemName === file.name)) {
            await AlertService.alert(`A file named "${file.name}" is already in the list.`);
            this.fileUploadWidget()?.reset();
            return;
        }
        if (list.length >= FILESTORE_POOL_MAX_FILES_PER_OBJECT) {
            await AlertService.alert(
                `The maximum number of files (${FILESTORE_POOL_MAX_FILES_PER_OBJECT}) has been reached.`,
            );
            this.fileUploadWidget()?.reset();
            return;
        }
        const blob = file.fileRef;
        if (!blob) {
            this.fileUploadWidget()?.reset();
            return;
        }
        const id = this.objectId();
        const ot = this.objectTypeId();
        this.uploading.set(true);
        this.uploadError.set(undefined);
        const [, err] = await S25Util.Maybe(
            AttachmentService.postFile(ot, id, blob, file.name, undefined, this.fileKey()),
        );
        this.uploading.set(false);
        this.fileUploadWidget()?.reset();
        if (err) {
            this.uploadError.set(S25Util.errorText(err, "Upload failed."));
            return;
        }
        await this.loadFiles();
    };

    private validatedUploadName(file: S25File): string | undefined {
        if (!file.name?.trim()) {
            return "File name cannot be empty.";
        }
        if (file.name.length > 112) {
            return "The file name is too long. File names can be no longer than 112 characters.";
        }
        return undefined;
    }

    protected fileKeyFor(f: FileItem): string | undefined {
        return f.fileKey ?? this.fileKey() ?? undefined;
    }

    protected async onDeleteClick(f: FileItem): Promise<void> {
        const id = this.objectId();
        const label = this.filesGroupLabel();
        const note = this.deleteConfirmNote()?.trim();
        const question = note
            ? `Delete "${f.itemName}" from ${label} files? ${note}`
            : `Delete "${f.itemName}" from ${label} files?`;
        if (!(await AlertService.confirm(question))) {
            return;
        }
        const guard = this.deleteGuard();
        if (guard) {
            const result = await guard(f);
            if (result.blockingCount > 0) {
                const names = result.blockingAttributeLabels?.map((s) => s?.trim()).filter(Boolean) ?? [];
                const namePart =
                    names.length === 1
                        ? ` Custom attribute: ${names[0]}.`
                        : names.length > 1
                          ? ` Custom attributes: ${names.join(", ")}.`
                          : "";
                const confirmed = await AlertService.confirm(
                    `This file is still referenced by ${result.blockingCount} custom attribute(s). Do you want to delete it anyway?`,
                );
                if (!confirmed) return;
            }
        }
        const [, err] = await S25Util.Maybe(
            AttachmentService.delFile(this.objectTypeId(), id, f.itemName, this.fileKey()),
        );
        if (err) {
            await AlertService.alert(S25Util.errorText(err, "Delete failed."));
            return;
        }
        await this.loadFiles();
    }
}
